import type {
  AdConfig,
  AdPlacementConfig,
  AdSlotConfig,
  AdTargetingConfig,
  GlobalAdConfig,
  PageAdConfig,
} from '@/app_lib/types/Ad';

const BYPASS_LEADERBOARD = 'adBypass:Leaderboard';
const BYPASS_SOV_SPONSORSHIP = 'adBypass:SOVSponsorship';
const BYPASS_HOMEPAGE = 'adBypass:Homepage';

/**
 * Build ad targeting object from pageAdConfig. The logic is to take a predefined list of fields,
 *  remove any that are null, and make all values of the object an array of strings.
 */
const buildTargeting = (pageAdConfig: PageAdConfig, pageView: number): AdTargetingConfig => {
  const { ctype, cid, ch, tags, topics, premiumCategory, loginStatus } = pageAdConfig;

  // Set up key/val targeting, filter out anything empty.
  const keyVals: { [key: string]: string | string[] | null } = {
    ch,
    cid,
    ctype,
    host: window.location.hostname,
    // biome-ignore lint/style/useNamingConvention: <external data>
    login_status: loginStatus || 'unknown',
    premiumCategory,
    pv: `${pageView}`,
    tags,
    topics,
  };

  // Remove null values
  const filteredKeyVals = Object.fromEntries(Object.entries(keyVals).filter(([, val]) => val !== null));

  // Convert everything to array
  const targeting = Object.keys(filteredKeyVals).reduce(
    (acc, key) => {
      const value = keyVals[key] as string | string[];
      acc[key] = Array.isArray(value) ? value : [value];
      return acc;
    },
    {} as { [key: string]: string[] },
  );

  return targeting;
};

const buildInitialSlots = (pageAdConfig: PageAdConfig): AdSlotConfig[] =>
  pageAdConfig.slots.map((slot) => {
    const config = {
      id: slot.id,
      index: slot.index,
      path:
        slot.placementName === 'Leaderboard'
          ? createLeaderboardPath(pageAdConfig)
          : createDefaultPath(pageAdConfig, slot.placementName),
      placementName: slot.placementName,
    };
    return config;
  });

const parsedPlacements = (pageAdConfig: PageAdConfig): AdPlacementConfig[] =>
  adConfig.placements.map((placement) => {
    if (
      placement.name === 'InStream' ||
      placement.name === 'InContent' ||
      placement.name === 'SimpleInStream' ||
      placement.name === 'Homepage-InStream'
    ) {
      return {
        ...placement,
        path: createInstreamPath(pageAdConfig, placement.name),
        refreshRate: adConfig.refreshRate,
      };
    }
    if (placement.name === 'RightRailFlex') {
      return {
        ...placement,
        path: createRightRailPath(pageAdConfig, placement.name),
        refreshRate: adConfig.refreshRate,
      };
    }
    if (placement.name === 'RightRailFlex_Articles') {
      return {
        ...placement,
        path: createRightRailArticlePath(pageAdConfig, placement.name),
        refreshRate: adConfig.refreshRate,
      };
    }

    return {
      ...placement,
      path: createDefaultPath(pageAdConfig, placement.name),
      refreshRate: adConfig.refreshRate,
    };
  });

/**
 * Create a DFP ad path from an array of strings.
 *
 * @param {array} parts Strings to include as parts of ad path.
 */
const createPath = (parts: Array<string | number>): string => {
  // Create the path for this ad using channel, content type, and placement.
  return parts
    .filter((pathPart: string | number) => {
      if (!pathPart) {
        return false;
      }
      return true;
    })
    .join('/');
};

export const ADS_ID = '';
export const ADS_NAME = process.env.GAM_NAME || '';
export const ADS_BASE_PATH = `/${ADS_NAME}`;

export const adConfig = {
  enabled: true,
  nonPersonalAds: true,
  path: ADS_BASE_PATH,
  placements: [
    {
      lazyLoad: true,
      name: 'Leaderboard',
      path: ADS_BASE_PATH,
    },
    {
      name: 'RightRailFlex',
      path: ADS_BASE_PATH,
    },
    {
      name: 'RightRail_Article',
      path: ADS_BASE_PATH,
    },
    {
      name: 'SimpleInStream',
      path: ADS_BASE_PATH,
    },
    {
      name: 'InStream',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Gallery',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Footer',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Homepage-InStream',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Newsletter-InStream',
      path: ADS_BASE_PATH,
    },
    {
      name: 'RightRailFlex_Articles',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Footer_Tout',
      path: ADS_BASE_PATH,
    },
    {
      name: 'TermArchive-Billboard',
      path: ADS_BASE_PATH,
    },
    {
      name: 'Homepage-SimpleInStream',
      path: ADS_BASE_PATH,
    },
    {
      name: 'InContent',
      path: ADS_BASE_PATH,
    },
    {
      name: 'StickyFooter',
      path: ADS_BASE_PATH,
    },
  ],
} as GlobalAdConfig;

export const createDefaultPath = (pageAdConfig: PageAdConfig, placementName: string) =>
  createPath([
    ADS_ID,
    pageAdConfig.defaultPath || ADS_NAME,
    pageAdConfig.invertChAndCtypeInPath ? pageAdConfig.ctype : pageAdConfig.ch,
    pageAdConfig.invertChAndCtypeInPath ? pageAdConfig.ch : pageAdConfig.ctype,
    placementName.toLowerCase(),
  ]);

// All company pages require a modiification for all the different kinds of ad.
// This is because the company page is a different path than the rest of the site
export const createLeaderboardPath = (pageAdConfig: PageAdConfig) => {
  const leaderboardPath =
    pageAdConfig.defaultPath === 'fortune/mobile' || pageAdConfig.defaultPath === 'fortune.well/mobile'
      ? 'adhesion'
      : 'leaderboard';

  const companyPath = pageAdConfig.ctype === 'company' ? 'company' : pageAdConfig.ch;
  return createPath([pageAdConfig.defaultPath, leaderboardPath, companyPath]);
};

export const createRightRailPath = (pageAdConfig: PageAdConfig, placementName: string) => {
  const RrPath = placementName === 'RightRailFlex' ? 'rail_300x250' : 'rightrailflex';
  const companyPath = pageAdConfig.ctype === 'company' ? 'company' : pageAdConfig.ch;
  return createPath([pageAdConfig.defaultPath, RrPath, companyPath]);
};

export const createRightRailArticlePath = (pageAdConfig: PageAdConfig, placementName: string) => {
  const RrArticlePath = placementName === 'RightRailFlex_Articles' ? 'rail_300xflex' : 'rightrailflex_articles';
  const companyPath = pageAdConfig.ctype === 'company' ? 'company' : pageAdConfig.ch;
  return createPath([pageAdConfig.defaultPath, RrArticlePath, companyPath]);
};

export const createInstreamPath = (pageAdConfig: PageAdConfig, placementName: string) => {
  let inStreamAdType =
    placementName === 'InStream' || placementName === 'Homepage-InStream' || placementName === 'InContent'
      ? 'incontent'
      : 'below_article';

  if (pageAdConfig.defaultPath === 'fortune/desktop' && placementName === 'InContent') {
    inStreamAdType = 'incontent-970';
  }

  const pagePath = ['people', 'company'].includes(pageAdConfig.ctype) ? pageAdConfig.ctype : pageAdConfig.ch;

  return createPath([pageAdConfig.defaultPath || ADS_NAME, inStreamAdType, pagePath]);
};

export const createAdTagPath = (channel: string, pathname: string) => {
  const handleChannel = () => {
    let ch = '';
    if (channel) {
      ch = channel.toLowerCase();
    } else if (pathname === '/fortune500/') {
      ch = 'fortune500';
    }
    return ch;
  };

  const verticals =
    pathname.match('/europe/') ||
    pathname.match('/asia/') ||
    pathname.match('/crypto/') ||
    pathname.match('/well/') ||
    pathname.match('/recommends/');

  const adTagPath = createPath([
    verticals ? `fortune.${verticals[0].replaceAll('/', '')}` : 'fortune',
    handleChannel(),
  ]);

  return adTagPath;
};

export const freeStarPlacementNameFinder = (
  placementName: string,
  sticky: boolean,
  index: number,
  path: string,
): string => {
  const trueIndex = index < 0 ? 0 : index;
  switch (true) {
    case placementName.includes('InStream'):
      return `fortunecom_infeed${trueIndex}`;
    case placementName.includes('RightRailFlex') && sticky && path.includes('crypto'):
      return 'fortunecom_crypto_siderail_sticky';
    case placementName.includes('RightRailFlex') && sticky:
      return 'fortunecom_siderail_sticky';
    case placementName.includes('RightRail_Article') && sticky:
      return 'fortunecom_siderail_article_sticky';
    case placementName.includes('RightRailFlex'):
      return `fortunecom_siderail_flex_${index}`;
    case placementName === 'Leaderboard' && path.includes('videos'):
      return 'fortunecom_videos_leaderboard_top';
    case placementName === 'Leaderboard':
      return 'fortunecom_leaderboard_top';
    case placementName === 'StickyFooter' && sticky:
      return 'fortunecom_sticky_footer';
    default:
      return `fortunecom_incontent${trueIndex}`;
  }
};

export const publisher = 'fortune-com';

export const checkForDirectSoldAd = (
  tags: string[] | undefined,
  ctype: string,
  placementName: string,
  placementConfig: AdPlacementConfig,
) => {
  const isBypassed =
    (placementName === 'Leaderboard' && tags?.includes(BYPASS_LEADERBOARD)) ||
    tags?.includes(BYPASS_SOV_SPONSORSHIP) ||
    (tags?.includes(BYPASS_HOMEPAGE) && ctype === 'homepage');

  return isBypassed
    ? {
        sizeMappings: placementConfig.sizeMappings || null,
        slotSize: placementConfig.sizeMappings?.flatMap((mapping) =>
          mapping.size.map((size) => `${size[0]}x${size[1]}`),
        ),
      }
    : { sizeMappings: null, slotSize: null };
};

export function buildConfig(pageAdConfig: PageAdConfig, pageView: number, adConsent: boolean): AdConfig {
  const { defaultPath } = pageAdConfig;

  const config = {
    ...adConfig,
    nonPersonalAds: !adConsent,
    path: defaultPath,
    placements: parsedPlacements(pageAdConfig),
    slots: buildInitialSlots(pageAdConfig),
    targeting: buildTargeting(pageAdConfig, pageView),
  };

  return config;
}

export const baseAdPlacementConfig = {
  desktopAdFrequency: 6,
  firstAdPlacement: 3,
  mobileAdFrequency: 4,
  showBetweenLatestsAndPopular: false,
  showEndArticleAd: false,
  showLeaderboardAd: true,
};

// NOTE: there's an interference here with mobile ads
export const NATIVO_POSITION = 10;
